import "./SideMenu.scss";
import { useState } from "react";
import {
  MotifVerticalNavigationContent,
  MotifVerticalNavigationMenu,
  MotifVerticalNavigationMenuItem,
  MotifVerticalNavigation,
} from "@ey-xd/motif-react";
import {
  AddToPhotos,
  HomeIcon,
  SubmitData,
  UserStar,
  PowerBiIcon,
  HelpIcon,
} from "../../icons";
import { routes } from "../../routes/routes.constatns";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getPowerBiLink } from "../../constants/powerBiLinks";
import {
  isEngagementManager as checkIsEngagementManager,
  isResourceManager as checkIsResourceManager,
  isGlobalReporting,
  hasUserManagementAccess,
  isGdsEmail,
} from "../../util/roleHelpers";
import { HelpModal } from "../HelpInfo/HelpModal";

export function SideMenu({ isSideMenuCollapsed }) {
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();
  const { domain } = useParams();
  const roleData = useSelector((state) => state.landing.roleData);
  const currentUser = useSelector((state) => state.user.user);
  const currentUserEmail = currentUser?.username || currentUser?.email || "";
  const isGdsUser = isGdsEmail(currentUserEmail);
  const coe = String(roleData?.coe || "").toUpperCase();
  const roles = roleData?.role;
  const isEngagementManager = checkIsEngagementManager(roles);
  const isResourceManager = checkIsResourceManager(roles);
  // When the URL has no domain (e.g. /userManagement), fall back to the
  // user's COE so domain-scoped menu items keep showing and navigate back
  // into the correct domain.
  const effectiveDomain = String(domain || coe || "").toUpperCase();
  const isCoeMatch = Boolean(effectiveDomain) && coe === effectiveDomain;
  const canAccessUserManagement = hasUserManagementAccess(roles);

  const location = useLocation();
  const isActive = (route) => location.pathname.includes("/" + route);

  /* istanbul ignore next */
  async function handleSideMenuClick(route) {
    if (route === routes.home) {
      navigate(`/${route}`);
    } else if (effectiveDomain) {
      navigate(`/${effectiveDomain}/${route}`);
    } else {
      navigate(`/${route}`);
    }
  }

  const menuItems = [
    <MotifVerticalNavigationMenuItem
      key={routes.home}
      icon={<HomeIcon />}
      label="Home"
      onClick={() => handleSideMenuClick(routes.home)}
      selected={isActive(routes.home)}
    >
      Home
    </MotifVerticalNavigationMenuItem>,
  ];

  if (isEngagementManager && isCoeMatch) {
    menuItems.push(
      <MotifVerticalNavigationMenuItem
        key={routes.engagementManager}
        icon={<AddToPhotos />}
        label="MyOrders"
        onClick={() => handleSideMenuClick(`${routes.engagementManager}`)}
        selected={isActive(routes.engagementManager)}
      >
        My Orders
      </MotifVerticalNavigationMenuItem>,
    );
  }

  if (isResourceManager && isCoeMatch) {
    const pmoLabel = effectiveDomain === "AOA" ? "PMO" : "Resource Manager";
    menuItems.push(
      <MotifVerticalNavigationMenuItem
        key={routes.resourceManager}
        icon={<SubmitData />}
        label={pmoLabel}
        onClick={() => handleSideMenuClick(`${routes.resourceManager}`)}
        selected={isActive(routes.resourceManager)}
      >
        {pmoLabel}
      </MotifVerticalNavigationMenuItem>,
    );
  }

  if (canAccessUserManagement && isCoeMatch) {
    menuItems.push(
      <MotifVerticalNavigationMenuItem
        key={routes.userManagement}
        icon={<UserStar />}
        label="UserManagement"
        onClick={() => navigate(`/${routes.userManagement}`)}
        selected={isActive(routes.userManagement)}
      >
        User Management
      </MotifVerticalNavigationMenuItem>,
    );
  }

  const isGlobalReportingRole = isGlobalReporting(roles);
  const powerBiLink = getPowerBiLink(roles, effectiveDomain);

  if (
    effectiveDomain &&
    powerBiLink &&
    isGdsUser &&
    (isCoeMatch || isGlobalReportingRole)
  ) {
    menuItems.push(
      <MotifVerticalNavigationMenuItem
        key="powerBiDashboard"
        icon={<PowerBiIcon />}
        label="Dashboard"
        onClick={() =>
          window.open(powerBiLink, "_blank", "noopener,noreferrer")
        }
      >
        Dashboard
      </MotifVerticalNavigationMenuItem>,
    );
  }

  menuItems.push(
    <MotifVerticalNavigationMenuItem
      key="help"
      className="app-side-menu__help-item"
      icon={<HelpIcon />}
      label="Help"
      onClick={() => setShowHelp(true)}
    >
      Help
    </MotifVerticalNavigationMenuItem>,
  );

  return (
    <>
      <MotifVerticalNavigation
        collapse={isSideMenuCollapsed}
        className="app-side-menu"
      >
        <MotifVerticalNavigationContent>
          <MotifVerticalNavigationMenu>{menuItems}</MotifVerticalNavigationMenu>
        </MotifVerticalNavigationContent>
      </MotifVerticalNavigation>
      <HelpModal show={showHelp} onClose={() => setShowHelp(false)} />
    </>
  );
}
